import { type AlboConfigurazioneTipoCampoDTO } from '@framework/common-web/register-records/frontoffice/api/schemas/alboConfigurazioneTipoCampoDTO.ts';
import { EnumAlboConfigurazioneTipoCampoDTOTipo } from '@framework/common-web/register-records/frontoffice/api/schemas/enumAlboConfigurazioneTipoCampoDTOTipo.ts';
import { RegisterRecordFieldVisibility } from '@framework/common-web/register-records/schema/RegisterRecordFieldVisibility.ts';
import { sortRegisterRecordsTypeField } from '@framework/common-web/register-records/utils/sortRegisterRecordsTypeField.ts';
import { LabelValueLine } from '@framework/common-web/ui/outcomes/LabelValueLine.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { LoaderSpinWrapper } from '@framework/ui/components/loader/LoaderSpin.style.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { AlignRow } from '@framework/ui/components/row/AlignRow.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import { Alert, Button, Flex } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledResult } from './StyledResult.tsx';
import { useGetUIPassInfo } from './useGetUIPassInfo.tsx';
import { useGetVerifyPassInfo } from './useGetVerifyPassInfo.ts';
import { useVerifyPassRegisterRecordsType } from './useVerifyPassRegisterRecordsType.ts';
import { VerifyPassNotFoundResult } from './VerifyPassNotFoundResult.tsx';
import { type IVerifyPassInput } from './VerifyPassPage.tsx';
import { VerifyPassStateTag } from './VerifyPassStateTag.tsx';

export interface IVerifyPassFormProps {
  code?: IVerifyPassInput;
  setShowInfo: (show: boolean) => void;
  setCode: (code: IVerifyPassInput | undefined) => void;
}

function renderCustomFieldValue(
  field: AlboConfigurazioneTipoCampoDTO,
  value: unknown,
) {
  if (value == null || value === '') return null;
  if (field.tipo === EnumAlboConfigurazioneTipoCampoDTOTipo.DATE) {
    return <ISODate date={String(value)} />;
  }
  return String(value);
}

/**
 * Componente per visualizzare le informazioni di un tesserino verificato
 */
export function VerifyPassForm(props: IVerifyPassFormProps) {
  const { code, setShowInfo, setCode } = props;
  const { t } = useTranslation();

  const handleReset = () => {
    setShowInfo(false);
    setCode(undefined);
  };

  const { record, error, isLoading } = useGetVerifyPassInfo(code);

  const registerRecordType = useVerifyPassRegisterRecordsType(
    record?.idTipoAlbo,
  );

  const passInfo = useGetUIPassInfo(record);

  const visibleCustomFields = useMemo(() => {
    if (!registerRecordType?.configurazioneCampi) return [];
    const visible = registerRecordType.configurazioneCampi.filter(
      field => field[RegisterRecordFieldVisibility.FO] && field.codice,
    );
    return sortRegisterRecordsTypeField(visible);
  }, [registerRecordType]);

  if (isLoading) {
    return (
      <LoaderSpinWrapper>
        <LoaderSpin />
      </LoaderSpinWrapper>
    );
  }

  if (error && error.status !== 404) {
    return (
      <ResponsiveCard>
        <Flex vertical gap={24} align="center">
          <Alert
            message={t(
              'verifica-tesserino.result.error.errore-caricamento',
              'Impossibile caricare le informazioni del tesserino',
            )}
            description={
              t('verifica-tesserino.result.error.errore', 'Errore:') +
              ' ' +
              getNetworkErrorMessage(error.message)
            }
            type="error"
            showIcon
          />
          <Button type="primary" size="large" onClick={handleReset}>
            {t('verifica-tesserino.torna-alla-ricerca', 'Torna alla ricerca')}
          </Button>
        </Flex>
      </ResponsiveCard>
    );
  }

  if (error?.status === 404 || !record) {
    return <VerifyPassNotFoundResult onReset={handleReset} />;
  }

  return (
    <ResponsiveCard>
      <StyledResult
        status={passInfo?.status}
        icon={passInfo?.icon}
        title={passInfo?.label}
        subTitle={passInfo?.subTitle}
      />

      {registerRecordType?.denominazione && (
        <LabelValueLine
          labelSpan={12}
          label={t('verifica-tesserino.tipo-tesserino', 'Tipo Tesserino:')}
          value={registerRecordType.denominazione}
        />
      )}

      <LabelValueLine
        labelSpan={12}
        label={t('verifica-tesserino.stato', 'Stato:')}
        value={<VerifyPassStateTag state={record?.statoIscrizione} />}
      />
      <LabelValueLine
        labelSpan={12}
        label={t('verifica-tesserino.codice', 'Codice:')}
        value={record?.codice}
      />
      <LabelValueLine
        labelSpan={12}
        label={t('verifica-tesserino.numero', 'Numero:')}
        value={record?.numero}
      />

      {visibleCustomFields.map(field => {
        const value = renderCustomFieldValue(
          field,
          record?.valori?.[field.codice ?? ''],
        );

        if (value == null) return null;

        return (
          <LabelValueLine
            key={field.codice}
            labelSpan={12}
            label={`${field.etichetta ?? field.codice}:`}
            value={value}
          />
        );
      })}

      <LabelValueLine
        labelSpan={12}
        label={t('verifica-tesserino.data-emissione', 'Data Inizio:')}
        value={<ISODate date={record?.dataEmissione} />}
      />

      <LabelValueLine
        labelSpan={12}
        label={t('verifica-tesserino.data-scadenza', 'Data Fine:')}
        value={<ISODate date={record?.dataScadenza} />}
      />

      <VerticalSpace />
      <AlignRow justify="center">
        <Button size="large" type="primary" onClick={handleReset}>
          {t('verifica-tesserino.nuova-ricerca', 'Nuova ricerca')}
        </Button>
      </AlignRow>
    </ResponsiveCard>
  );
}
